import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Eye,
  Info,
  List,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserPlus,
} from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../../services/api";
import SearchableSelect from "../SearchableSelect";
import { formatInr } from "./runPayrollUtils";
import { displayLabel, employeeDisplayName } from "../../../utils/displayLabel";
import LeftEmployeesFnfModal from "./LeftEmployeesFnfModal";
import { PAYROLL_ACTION_CHROME, Icon3dFnf } from "./payrollActionChrome";
import {
  ACTION_CARD,
  ACTION_CARD_FOOTER,
  ACTION_DROPDOWN_RESERVE,
  ACTION_SELECT_WELL,
} from "../shared/actionUiChrome";
import { usePermissions } from "../../../rbac/PermissionsContext";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "ready", label: "Ready" },
  { value: "paid", label: "Paid" },
  { value: "hold", label: "Hold" },
];

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "paid") return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (s === "ready") return "bg-sky-50 text-sky-800 border-sky-200";
  if (s === "hold") return "bg-amber-50 text-amber-900 border-amber-200";
  if (s === "draft") return "bg-slate-100 text-slate-700 border-slate-200";
  return "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]";
}

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function InfoTip({ text }) {
  if (!text) return null;
  return (
    <span
      title={text}
      className="inline-flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center text-[hsl(var(--ds-muted-fg))]"
      aria-label={text}
    >
      <Info size={14} strokeWidth={1.75} className="h-3.5 w-3.5" />
    </span>
  );
}

/**
 * Process F&F — renders inside Payroll Actions page body (keeps nav / sidebars).
 */
export default function ProcessFnfWorkspace({
  darkMode = false,
  payMonth,
  onClose,
  embedded = true,
  hideBack = false,
}) {
  const { hasScreenAccess, isAdmin } = usePermissions();
  const canEditFnf =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_process_fnf", "edit");
  const canApproveFnf =
    isAdmin ||
    hasScreenAccess("payroll", "pay_act_process_fnf", "approve");
  const [panel, setPanel] = useState("process");
  const [eligible, setEligible] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [listQ, setListQ] = useState("");
  const [listStatus, setListStatus] = useState("all");
  const [activeEmployee, setActiveEmployee] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const PAGE_SIZE = 50;

  const border = PAYROLL_ACTION_CHROME.borderClass;
  const muted = "text-[hsl(var(--ds-muted-fg))]";

  const loadEligible = useCallback(async () => {
    setLoadingEligible(true);
    try {
      const res = await payrollOpsService.listFnfEligibleEmployees();
      setEligible(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load eligible employees");
      setEligible([]);
    } finally {
      setLoadingEligible(false);
    }
  }, []);

  const loadSettlements = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await payrollOpsService.listFnfSettlements({
        status: listStatus,
        q: listQ.trim() || undefined,
      });
      setSettlements(Array.isArray(res?.data) ? res.data : []);
      setVisibleCount(PAGE_SIZE);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load F&F list");
      setSettlements([]);
      setVisibleCount(PAGE_SIZE);
    } finally {
      setLoadingList(false);
    }
  }, [listStatus, listQ]);

  useEffect(() => {
    loadEligible();
  }, [loadEligible]);

  useEffect(() => {
    if (panel !== "list") return undefined;
    const t = setTimeout(() => {
      loadSettlements();
    }, listQ ? 280 : 0);
    return () => clearTimeout(t);
  }, [panel, listStatus, loadSettlements, listQ]);

  const employeeOptions = useMemo(
    () =>
      (eligible || []).map((e) => ({
        value: String(e.employeeId || e.id),
        label: [
          displayLabel(e.empCode, ""),
          employeeDisplayName(e),
          e.relievedDate || e.lastWorkingDay
            ? `LWD ${formatDate(e.relievedDate || e.lastWorkingDay)}`
            : null,
        ]
          .filter(Boolean)
          .join(" — "),
      })),
    [eligible]
  );

  const selectedEligible = useMemo(
    () =>
      (eligible || []).find((e) => String(e.employeeId || e.id) === String(employeeId)),
    [eligible, employeeId]
  );

  const openSettlement = (emp, { month } = {}) => {
    const eid = Number(emp?.employeeId || emp?.id);
    if (!eid || Number.isNaN(eid)) {
      toast.error("Invalid employee on this settlement");
      return;
    }
    const booking = String(month || emp?.payMonth || payMonth || "").slice(0, 7);
    if (!booking) {
      toast.error("Booking month is required");
      return;
    }
    setActiveEmployee({
      employeeId: eid,
      empCode: displayLabel(emp.empCode, ""),
      name: employeeDisplayName(emp),
      lastWorkingDay: emp.lastWorkingDay || emp.relievedDate || null,
      payMonth: booking,
    });
  };

  const handleProcess = () => {
    if (!canEditFnf) {
      toast.error("No permission to process F&F. Grant Edit on Process F&F.");
      return;
    }
    if (!selectedEligible) {
      toast.error("Select a relieved employee whose F&F is not done");
      return;
    }
    openSettlement(selectedEligible, { month: payMonth });
  };

  const handleDelete = async (row) => {
    if (!canEditFnf && !canApproveFnf) {
      toast.error("No permission to delete F&F settlements.");
      return;
    }
    if (!row?.id) return;
    const ok = window.confirm(
      `Delete F&F for ${row.empCode || ""} ${row.name || ""} (${row.payMonth || ""})?\nThis cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await payrollOpsService.deleteFnfSettlement(row.id);
      toast.success("F&F settlement deleted");
      await Promise.all([loadSettlements(), loadEligible()]);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const onSettlementSaved = async () => {
    setActiveEmployee(null);
    await Promise.all([loadEligible(), panel === "list" ? loadSettlements() : Promise.resolve()]);
  };

  const visibleSettlements = useMemo(
    () => settlements.slice(0, visibleCount),
    [settlements, visibleCount]
  );
  const hasMoreSettlements = settlements.length > visibleCount;

  void embedded;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {!hideBack ? (
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
            >
              <ArrowLeft size={14} /> Actions
            </button>
          ) : null}
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-[hsl(var(--ds-primary)/0.08)] p-1 pace-action-rail-icon">
            <Icon3dFnf size={24} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight text-[hsl(var(--ds-fg))]">
              Process F&amp;F
            </h2>
            <p className={`text-[11px] ${muted}`}>Booking month {payMonth || "—"}</p>
          </div>
        </div>

        <div className="inline-flex rounded-xl border border-[hsl(var(--ds-primary)/0.35)] bg-[hsl(var(--ds-surface))] p-0.5">
          <button
            type="button"
            onClick={() => setPanel("process")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              panel === "process"
                ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
            }`}
          >
            <UserPlus size={14} /> Process
          </button>
          <button
            type="button"
            onClick={() => setPanel("list")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              panel === "list"
                ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
            }`}
          >
            <List size={14} /> F&amp;F List
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {panel === "process" ? (
          <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-start overflow-visible pb-2 pt-1">
            <div className={ACTION_CARD}>
              <div className="relative z-0 flex flex-1 flex-col space-y-4 p-5 pb-3">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-sm font-semibold tracking-tight text-[hsl(var(--ds-fg))]">
                    Select employee to process
                  </h3>
                  <InfoTip text="Only Relieved, Terminated, or Resigned employees (or those with a relieving date) appear here. Use F&F List for existing settlements including cleanup of mistaken rows." />
                </div>

                {loadingEligible ? (
                  <div className={`flex items-center justify-center gap-2 py-10 text-sm ${muted}`}>
                    <Loader2 size={16} className="animate-spin" /> Loading eligible leavers…
                  </div>
                ) : employeeOptions.length === 0 ? (
                  <div className={`rounded-xl border border-dashed px-4 py-10 text-center ${border}`}>
                    <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">
                      No employees pending F&amp;F
                    </p>
                    <p className={`mt-1 text-xs ${muted}`}>
                      All relieved employees either have F&amp;F paid via disbursement, or none are
                      marked relieved yet.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className={ACTION_SELECT_WELL}>
                      <SearchableSelect
                        id="process-fnf-eligible"
                        label="Employee"
                        value={employeeId}
                        onChange={(e) => setEmployeeId(String(e?.target?.value ?? ""))}
                        staticOptions={employeeOptions}
                        darkMode={darkMode}
                        compact
                        required
                        placeholder="Search code or name…"
                        menuPlacement="bottom"
                        menuPortalTarget={null}
                        inActionCard
                      />
                    </div>
                    <div className={ACTION_DROPDOWN_RESERVE} aria-hidden="true" />
                    {selectedEligible ? (
                      <div
                        className={`grid grid-cols-2 gap-3 rounded-xl border px-3.5 py-3 text-xs ${border} bg-[hsl(var(--ds-primary)/0.06)]`}
                      >
                        <div>
                          <div className={muted}>Status</div>
                          <div className="mt-0.5 font-semibold text-[hsl(var(--ds-fg))]">
                            {selectedEligible.employmentStatus || "—"}
                          </div>
                        </div>
                        <div>
                          <div className={muted}>Last working day</div>
                          <div className="mt-0.5 font-semibold text-[hsl(var(--ds-fg))]">
                            {formatDate(
                              selectedEligible.lastWorkingDay || selectedEligible.relievedDate
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <p className={`text-[11px] ${muted}`}>
                      {employeeOptions.length} leaver
                      {employeeOptions.length === 1 ? "" : "s"} pending F&amp;F
                    </p>
                  </>
                )}
              </div>
              <div className={ACTION_CARD_FOOTER}>
                {employeeOptions.length === 0 && !loadingEligible ? (
                  <button
                    type="button"
                    onClick={() => setPanel("list")}
                    className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
                  >
                    <List size={14} /> Open F&amp;F List
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={loadEligible}
                    className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
                  >
                    <RefreshCw size={13} /> Refresh
                  </button>
                )}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPanel("list")}
                    className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
                  >
                    <List size={13} /> View list
                  </button>
                  <button
                    type="button"
                    disabled={
                      !canEditFnf ||
                      !employeeId ||
                      loadingEligible ||
                      employeeOptions.length === 0
                    }
                    onClick={handleProcess}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[hsl(var(--ds-primary))] px-4 py-2 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] shadow-sm disabled:opacity-45"
                  >
                    <Plus size={14} /> Process F&amp;F
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="relative min-w-[14rem] flex-1">
                <Search
                  size={13}
                  className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`}
                />
                <input
                  type="search"
                  value={listQ}
                  onChange={(e) => setListQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") loadSettlements();
                  }}
                  placeholder="Search code, name, dept, month…"
                  className={`h-9 w-full rounded-lg border bg-[hsl(var(--ds-surface))] pl-8 pr-3 text-xs ${border}`}
                />
              </div>
              <select
                value={listStatus}
                onChange={(e) => setListStatus(e.target.value)}
                className={`h-9 rounded-lg border bg-[hsl(var(--ds-surface))] px-2 text-xs ${border}`}
                aria-label="Status filter"
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={loadSettlements}
                className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold ${border} hover:bg-[hsl(var(--ds-muted))]`}
              >
                <RefreshCw size={13} /> Refresh
              </button>
              <button
                type="button"
                onClick={() => setPanel("process")}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[hsl(var(--ds-primary))] px-3 text-xs font-semibold text-[hsl(var(--ds-primary-fg))]"
              >
                <Plus size={13} /> New
              </button>
            </div>

            <div className={`min-h-0 flex-1 overflow-auto rounded-xl border bg-[hsl(var(--ds-surface))] ${border}`}>
              <table className="min-w-full text-left text-xs">
                <thead className="sticky top-0 z-[1] bg-[hsl(var(--ds-primary)/0.08)]">
                  <tr className="border-b border-[hsl(var(--ds-primary)/0.2)]">
                    <th className="px-3 py-2.5 font-semibold">Sl No</th>
                    <th className="px-3 py-2.5 font-semibold">Emp Code</th>
                    <th className="px-3 py-2.5 font-semibold">Name</th>
                    <th className="px-3 py-2.5 font-semibold">Designation</th>
                    <th className="px-3 py-2.5 font-semibold">Department</th>
                    <th className="px-3 py-2.5 font-semibold">LWD</th>
                    <th className="px-3 py-2.5 font-semibold">Month</th>
                    <th className="px-3 py-2.5 font-semibold">Method</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Net</th>
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingList ? (
                    <tr>
                      <td colSpan={11} className={`px-3 py-12 text-center ${muted}`}>
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> Loading settlements…
                        </span>
                      </td>
                    </tr>
                  ) : settlements.length === 0 ? (
                    <tr>
                      <td colSpan={11} className={`px-3 py-12 text-center ${muted}`}>
                        No F&amp;F settlements match this filter.
                      </td>
                    </tr>
                  ) : (
                    visibleSettlements.map((row, idx) => {
                      const st = String(row.status || "").toLowerCase();
                      return (
                        <tr
                          key={row.id}
                          className="border-b border-[hsl(var(--ds-primary)/0.1)] last:border-0 hover:bg-[hsl(var(--ds-primary)/0.04)]"
                        >
                          <td className="px-3 py-2.5 tabular-nums">{idx + 1}</td>
                          <td className="px-3 py-2.5 font-semibold">
                            {displayLabel(row.empCode, "")}
                          </td>
                          <td className="px-3 py-2.5">{employeeDisplayName(row)}</td>
                          <td className="px-3 py-2.5">{displayLabel(row.designation)}</td>
                          <td className="px-3 py-2.5">{displayLabel(row.department)}</td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            {formatDate(row.lastWorkingDay)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            {row.payMonth || "—"}
                          </td>
                          <td className="px-3 py-2.5">{row.processMethodLabel || "—"}</td>
                          <td className="px-3 py-2.5 text-right font-medium tabular-nums">
                            {formatInr(row.netAmount)}
                          </td>
                          <td className="px-3 py-2.5">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(
                                row.status
                              )}`}
                            >
                              {displayLabel(row.status, "draft")}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                title="View"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openSettlement(row, { month: row.payMonth });
                                }}
                                className={`rounded-md border p-1.5 ${border} hover:bg-[hsl(var(--ds-muted))]`}
                              >
                                <Eye size={13} />
                              </button>
                              {canEditFnf ? (
                                <button
                                  type="button"
                                  title={
                                    st === "paid" || st === "hold"
                                      ? "Paid / hold — open read-only"
                                      : "Edit"
                                  }
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openSettlement(row, { month: row.payMonth });
                                  }}
                                  className={`rounded-md border p-1.5 ${border} hover:bg-[hsl(var(--ds-muted))]`}
                                >
                                  <Pencil size={13} />
                                </button>
                              ) : null}
                              {canEditFnf || canApproveFnf ? (
                                <button
                                  type="button"
                                  title="Delete (blocked only if paid disbursement exists)"
                                  disabled={deletingId === row.id}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDelete(row);
                                  }}
                                  className={`rounded-md border p-1.5 text-red-700 ${border} hover:bg-red-50 disabled:opacity-35`}
                                >
                                  {deletingId === row.id ? (
                                    <Loader2 size={13} className="animate-spin" />
                                  ) : (
                                    <Trash2 size={13} />
                                  )}
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {!loadingList && settlements.length > 0 ? (
              <div className="relative mt-2 flex shrink-0 items-center justify-between gap-2">
                <p className={`text-[11px] ${muted}`}>
                  Showing {visibleSettlements.length} of {settlements.length} settlement
                  {settlements.length === 1 ? "" : "s"}
                </p>
                {hasMoreSettlements ? (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--ds-primary)/0.4)] bg-white px-3.5 py-1.5 text-[11px] font-semibold text-[hsl(var(--ds-primary))] shadow-md hover:bg-[hsl(var(--ds-primary)/0.06)] dark:bg-[hsl(var(--ds-surface))]"
                  >
                    Load more
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {activeEmployee ? (
        <LeftEmployeesFnfModal
          darkMode={darkMode}
          payMonth={activeEmployee.payMonth || payMonth}
          employee={activeEmployee}
          onClose={() => setActiveEmployee(null)}
          onSaved={onSettlementSaved}
          forceReadOnly={!canEditFnf}
          canMarkPaid={canApproveFnf}
        />
      ) : null}
    </div>
  );
}
